import { Account, BalanceSnapshot, PeriodSummary } from "../types/spend-tracker";

export function computePeriodSummary(
  currentBalances: BalanceSnapshot[],
  previousBalances: BalanceSnapshot[],
  accounts: Account[]
): PeriodSummary {
  const accountMap = new Map(accounts.map(a => [a.id, a]));

  // Helper to sum balances by account type
  const sumByType = (balances: BalanceSnapshot[], type: string): number => {
    return balances.reduce((sum, b) => {
      const account = accountMap.get(b.accountId);
      return account?.type === type ? sum + b.balance : sum;
    }, 0);
  };

  // Current period totals
  const totalCash = sumByType(currentBalances, "cash");
  const totalCredit = sumByType(currentBalances, "credit");
  const totalInvestments = sumByType(currentBalances, "investment");
  const netWorth = totalCash + totalCredit + totalInvestments;

  // Previous period totals
  const prevCash = sumByType(previousBalances, "cash");
  const prevCredit = sumByType(previousBalances, "credit");
  const prevInvestments = sumByType(previousBalances, "investment");
  const prevNetWorth = prevCash + prevCredit + prevInvestments;

  // Changes
  const cashChange = totalCash - prevCash;
  const creditChange = totalCredit - prevCredit;
  const investmentChange = totalInvestments - prevInvestments;
  const netWorthChange = netWorth - prevNetWorth;

  // Estimated spend: cash went down or credit went more negative
  const estimatedSpend = Math.max(0, -cashChange) + Math.max(0, -creditChange);

  // Approximate investing: investment balance increased
  const approxInvesting = Math.max(0, investmentChange);

  return {
    netWorth,
    netWorthChange,
    estimatedSpend,
    approxInvesting,
    totalCash,
    totalCredit,
    totalInvestments,
  };
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatCurrencyChange(value: number): string {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}